/**
 * The left rail.
 *
 * 56px of icons, not a 240px drawer. A trading terminal's horizontal space
 * belongs to the chart and the tables; a permanent quarter-width nav column
 * is space the numbers do not get. Labels arrive on hover as a tooltip, and
 * every destination has a `g`-prefixed hotkey for people who never reach for
 * the mouse.
 *
 * It can be expanded, and that preference persists — but it starts narrow.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { useUiStore } from '@/store/uiStore';

import { Tooltip } from '../ui/Tooltip';
import { SHOWN_NAV_ITEMS } from './navigation';

export function Sidebar() {
  const collapsed = useUiStore((state) => state.sidebarCollapsed);
  const toggle = useUiStore((state) => state.toggleSidebar);

  return (
    <nav
      aria-label="Primary"
      className={cn(
        'hidden shrink-0 flex-col border-r border-border bg-surface-1 md:flex',
        collapsed ? 'w-[var(--density-rail)]' : 'w-[var(--density-rail-expanded)]',
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-1.5">
        {SHOWN_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const link = (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex h-7 items-center gap-2 rounded px-2 transition-colors',
                  collapsed && 'justify-center px-0',
                  isActive
                    ? 'bg-accent-weak text-accent'
                    : 'text-mid hover:bg-surface-2 hover:text-hi',
                )
              }
            >
              <Icon size={16} className="shrink-0" aria-hidden />
              {!collapsed && (
                <span className="truncate text-[12px] font-medium">{item.label}</span>
              )}
            </NavLink>
          );

          return collapsed ? (
            <Tooltip
              key={item.to}
              side="right"
              delay={120}
              as="block"
              content={
                <div className="space-y-0.5">
                  <p className="font-semibold">{item.label}</p>
                  <p className="text-mid">{item.description}</p>
                  <p className="num text-lo">g then {item.hotkey}</p>
                </div>
              }
            >
              {link}
            </Tooltip>
          ) : (
            link
          );
        })}
      </div>

      <button
        type="button"
        onClick={toggle}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        className={cn(
          'flex h-7 shrink-0 items-center gap-2 border-t border-border px-2 text-[10.5px] text-lo',
          'transition-colors hover:bg-surface-2 hover:text-hi',
          collapsed && 'justify-center px-0',
        )}
      >
        {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
        {!collapsed && 'Collapse'}
      </button>
    </nav>
  );
}
